/**
 * DviWorkflowStepper - Visual navigation for the 7-stage DVI workflow
 * 
 * Stages:
 * 0 - Session: Create/manage DVI session
 * 1 - Capture: Upload and manage media
 * 2 - Group: Organize media into finding groups
 * 3 - Analyze: Run AI analysis on groups
 * 4 - Evidence: Build evidence packets
 * 5 - Estimate: Create and review estimate draft
 * 6 - Publish: Share with customer
 */

import { cn } from '@/lib/utils';
import { 
  Circle, 
  Camera, 
  Layers, 
  Sparkles, 
  FileCheck, 
  FileText, 
  Share2,
  Check
} from 'lucide-react';

export type WorkflowStage = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface StageConfig {
  id: WorkflowStage;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STAGES: StageConfig[] = [
  { id: 0, label: 'Session', icon: Circle },
  { id: 1, label: 'Capture', icon: Camera },
  { id: 2, label: 'Group', icon: Layers },
  { id: 3, label: 'Analyze', icon: Sparkles },
  { id: 4, label: 'Evidence', icon: FileCheck },
  { id: 5, label: 'Estimate', icon: FileText },
  { id: 6, label: 'Publish', icon: Share2 },
];

interface DviWorkflowStepperProps {
  currentStage: WorkflowStage;
  completedStages?: WorkflowStage[];
  onStageClick?: (stage: WorkflowStage) => void;
  vertical?: boolean;
  className?: string;
}

export function DviWorkflowStepper({
  currentStage,
  completedStages = [],
  onStageClick,
  vertical = false,
  className,
}: DviWorkflowStepperProps) {
  return (
    <div 
      className={cn(
        'flex gap-1',
        vertical ? 'flex-col' : 'flex-row overflow-x-auto',
        className
      )}
    >
      {STAGES.map((stage, index) => {
        const isActive = stage.id === currentStage;
        const isCompleted = completedStages.includes(stage.id);
        const Icon = stage.icon;
        
        return (
          <button
            key={stage.id}
            onClick={() => onStageClick?.(stage.id)}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors shrink-0',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
              isActive 
                ? 'bg-primary text-primary-foreground' 
                : isCompleted 
                  ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80',
              !onStageClick && 'cursor-default',
            )}
            disabled={!onStageClick}
            type="button"
          >
            <div className="relative">
              {isCompleted && !isActive ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Icon className="h-3.5 w-3.5" />
              )}
            </div>
            <span className={cn(vertical ? '' : 'hidden sm:inline')}>
              {stage.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
